import React, { useState, useRef, useEffect } from 'react';
import './style.css';

const ReviewImageUploader = ({ defaultImages, onImagesChange, maxFileSize = 5, maxFiles = 5, allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'] }) => {
  const [images, setImages] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (defaultImages) {
      setImages(defaultImages);
    }
  }, [defaultImages]);

  // Convert MB to bytes
  const maxFileSizeBytes = maxFileSize * 1024 * 1024;

  const handleFileValidation = (files) => {
    const validFiles = [];
    const errorMessages = [];

    // Check if adding these files would exceed the max file count
    if (images.length + files.length > maxFiles) {
      errorMessages.push(`You can only upload a maximum of ${maxFiles} images.`);
      return { validFiles, errorMessages };
    }

    Array.from(files).forEach(file => {
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        errorMessages.push(`${file.name}: File type not supported. Please upload JPG or PNG files.`);
        return;
      }

      // Validate file size
      if (file.size > maxFileSizeBytes) {
        errorMessages.push(`${file.name}: File size exceeds ${maxFileSize}MB limit.`);
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, errorMessages };
  };

  const handleFiles = (files) => {
    const { validFiles, errorMessages } = handleFileValidation(files);

    if (errorMessages.length > 0) {
      setErrors(errorMessages);
      setTimeout(() => setErrors([]), 5000);
    }

    if (validFiles.length > 0) {
      const newImagesArray = [...images];

      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          newImagesArray.push({
            file,
            preview: e.target.result,
            id: Date.now() + Math.random().toString(36).substring(2, 15)
          });

          // Only update state and call callback once all files are processed
          if (newImagesArray.length === images.length + validFiles.length) {
            setImages(newImagesArray);
            // onImagesChange && onImagesChange(newImagesArray.map(img => {
            //   return img.preview.includes("uploads") ? img : img.file
            // }));

            onImagesChange && onImagesChange(newImagesArray)
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleButtonClick = (e) => {
    e.stopPropagation(); // Stop propagation to prevent multiple clicks
    fileInputRef.current.click();
  };

  // Separate click handler for the dropzone
  const handleDropzoneClick = (e) => {
    // Only trigger if the click is directly on the dropzone (not on children)
    if (e.target === e.currentTarget) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveImage = (idToRemove) => {
    const imageToRemove = images.find(image => image.id === idToRemove);
    const filteredImages = images.filter(image => image.id !== idToRemove);
    setImages(filteredImages);
    // debugger;
    // if (imageToRemove.preview.includes("uploads")) {
      onImagesChange && onImagesChange(filteredImages);
    // } 
    // else {
    //   onImagesChange && onImagesChange(filteredImages.map(img => img.file));
    // }
  };

  return (
    <div className="image-uploader-container">
      <div
        className={`image-uploader-dropzone ${dragActive ? 'active' : ''}`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={handleDropzoneClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedTypes.join(',')}
          multiple
          onChange={handleFileChange}
          className="file-input"
        />
        <div className="upload-message" onClick={(e) => e.stopPropagation()}>
          <div className="upload-icon">📷</div>
          <p>Drag and drop images here or</p>
          <button
            type="button"
            className="btn btn-upload btn-sm"
            onClick={handleButtonClick}
          >
            Browse Files
          </button>
          <p className="upload-info">
            Supported formats: JPG, PNG | Max file size: {maxFileSize}MB | Max {maxFiles} images
          </p>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="error-container">
          {errors.map((error, index) => (
            <p key={index} className="error-message">{error}</p>
          ))}
        </div>
      )}

      {images.length > 0 && (
        <div className="image-preview-container">
          <h4>Selected Images ({images.length}/{maxFiles})</h4>
          <div className="image-grid">
            {images.map((image) => (
              <div key={image.id} className="image-preview-item">
                <img src={image.preview} alt="Preview" className="image-preview" />
                <button
                  type="button"
                  className="remove-button"
                  onClick={() => handleRemoveImage(image.id)}
                >
                  ×
                </button>
                <p className="image-filename">{image.file.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewImageUploader;